import { useEffect, useRef, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { useSearchParams } from 'react-router';
import { portalChatFaq } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Skeleton } from '../../../ds/components';
import { useIsMobile } from '../../../ds/hooks';
import { usePortalTickets } from '../api';
import frame from '../frame/Portal.module.css';
import { PageError } from '../frame/PageState';
import { usePortal } from '../frame/PortalLayout';
import { TicketCardView } from './ChatCards';
import { ChatPanel } from './ChatPanel';
import { TicketSheet } from './TicketSheet';
import { useChat } from './useChat';
import styles from './Questions.module.css';

/** „Ihre Anliegen" (own heading): the customer's support tickets, each opening the ticket sheet. */
function Tickets({ onOpen }: { onOpen: (id: string) => void }) {
  const tickets = usePortalTickets();
  if (tickets.isPending) return <Skeleton height={96} />;
  if (tickets.isError) {
    return (
      <PageError
        what="Ihre Anliegen"
        error={tickets.error}
        onRetry={() => void tickets.refetch()}
        retrying={tickets.isFetching}
      />
    );
  }
  if (tickets.data.items.length === 0) return null;
  return (
    <section className={styles.tickets} aria-labelledby="portal-tickets-title">
      <h2 className={styles.ticketsTitle} id="portal-tickets-title">
        Ihre Anliegen
      </h2>
      <ul className={styles.ticketList}>
        {tickets.data.items.map((ticket) => (
          <li key={ticket.id}>
            <button
              type="button"
              className={styles.ticketButton}
              onClick={() => onOpen(ticket.id)}
              aria-label={`Ticket ${ticket.numberLabel} öffnen: ${ticket.title}`}
            >
              <TicketCardView ticket={ticket} compact />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * `/portal/:orgSlug/questions` — Mandantenportal 22: FAQ tiles (a tile sends its question to the chat), the
 * customer's tickets and the chat docked on the right (440 px; the cross collapses it to „Chat öffnen", own). Below
 * 768 px the chat is the full-screen sheet of the frame. `?intent=appointment` („Beratungstermin buchen") opens the
 * chat and sends the quick reply „Termin mit <Berater>".
 */
export function QuestionsPage() {
  const { me, setChatOpen } = usePortal();
  const mobile = useIsMobile();
  const controller = useChat();
  const [collapsed, setCollapsed] = useState(false);
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const intentHandled = useRef(false);
  const advisorName = me.advisor?.name ?? 'Ihren Berater';

  useEffect(() => {
    if (params.get('intent') !== 'appointment' || intentHandled.current) return;
    intentHandled.current = true;
    const copy = new URLSearchParams(params);
    copy.delete('intent');
    setParams(copy, { replace: true });
    setCollapsed(false);
    if (mobile) setChatOpen(true);
    void controller.sendQuickReply('appointment');
  }, [params, setParams, mobile, setChatOpen, controller]);

  function ask(question: string) {
    setCollapsed(false);
    if (mobile) setChatOpen(true);
    void controller.sendText(question);
  }

  const docked = !mobile && !collapsed;
  return (
    <div className={clsx(styles.layout, docked && styles.layoutDocked)}>
      <div className={styles.left}>
        <div className={frame.pageHead}>
          <div>
            <h1 className={frame.pageTitle}>Fragen</h1>
            <div className={frame.pageText}>
              Häufige Antworten sofort. Alles andere geht an {advisorName}.
            </div>
          </div>
          {!mobile && collapsed ? (
            <button
              type="button"
              className={clsx('pressable', frame.primary)}
              onClick={() => setCollapsed(false)}
            >
              <MessageSquare size={16} strokeWidth={1.75} aria-hidden="true" />
              Chat öffnen
            </button>
          ) : null}
        </div>
        <div className={styles.faq}>
          {portalChatFaq.map((question) => (
            <button
              key={question}
              type="button"
              className={styles.faqTile}
              onClick={() => ask(question)}
              disabled={controller.sending}
            >
              {question}
            </button>
          ))}
        </div>
        <Tickets onOpen={setTicketId} />
      </div>
      {docked ? (
        <ChatPanel
          controller={controller}
          onClose={() => setCollapsed(true)}
          onOpenTicket={setTicketId}
          className={styles.docked}
        />
      ) : null}
      <TicketSheet ticketId={ticketId} onClose={() => setTicketId(null)} />
    </div>
  );
}
